import { TranslateService } from '@ngx-translate/core';
import { Injectable, OnDestroy } from '@angular/core';
import { Subscription, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MessengerService } from './messenger.service';
import { Channel } from 'src/app/channel/model/channel';
import { Cacheable } from 'ts-cacheable';
import { SettingService } from './setting.service';

@Injectable({
  providedIn: 'root'
})
export class ChannelService implements OnDestroy
{
  /**
   * List of Performance selection
   */
  public channelPerformances: Array<{ text: string; value: string; sort: number }> = [
    { text: 'Red', value: 'danger', sort: 1 },
    { text: 'Orange', value: 'warning', sort: 2 },
    { text: 'Green', value: 'success', sort: 3 },
    { text: 'Blue', value: 'neutral', sort: 4 }
  ];

  /**
   * List of Channel Statuses selection
   */
  public channelStatuses: Array<{ text: string; value: string }> = [
    { text: 'Offline', value: 'Offline' },
    { text: 'Online', value: 'Online' },
    { text: 'Simulated', value: 'Simulated' },
    { text: 'Archived', value: 'Archived' }
  ];

  /**
   * List of Channel Statuses selection with disabled attribute
   */
  public channelStatusesArchived: Array<{ text: string; value: string; disabled: boolean }> = [
    { text: 'Offline', value: 'Offline', disabled: true },
    { text: 'Online', value: 'Online', disabled: true },
    { text: 'Simulated', value: 'Simulated', disabled: true },
    { text: 'Archived', value: 'Archived', disabled: false }
  ];

  /**
   * List of Channel Sorts selection
   */
  public channelSorts: Array<{ text: string; value: string }> = [
    { text: 'Date Created', value: 'createdOn' },
    { text: 'Number of Open Cases', value: 'openCases' },
    { text: 'Number of Open Tasks', value: 'totalTasks' },
    { text: 'Number of Late Tasks', value: 'lateTasks' },
    { text: 'Channel Name', value: 'name' }
  ];

  /**
   * List of Channel Sorts selection
   */
  public channelSortsDashboard: Array<{ text: string; value: string }> = [
    { text: 'Number of Open Cases', value: 'openCases' },
    { text: 'Number of Closed Tasks', value: 'closedCases' },
    { text: 'Number of Late Tasks', value: 'lateTasks' },
    { text: 'Channel Name', value: 'channelName' }
  ];

  private messagingLanguageChangedSubscription: Subscription;
  private allowMultipleChannelsEnrollment = false;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private settingService: SettingService,
    private messengerService: MessengerService)
  {
    this.loadSettings();
    this.loadLocalizations();
    this.messagingLanguageChangedSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type && message.type === 'wsSettingsUpdated')
        {
          this.loadSettings();
        }
        else
          if (message && message.type && message.type === 'languageChanged')
          {
            this.loadLocalizations();
          }
      }
    });
  }

  /**
   * Get a Channel by ID from REST service.
   *
   * @param id Channel ID
   */
  @Cacheable()
  public getCacheChannel(id: number): Observable<Channel>
  {
    return this.http.get<Channel>('/channels/' + id);
  }

  /**
   * Get all Channels from REST service.
   */
  @Cacheable()
  public getCacheMinimalChannels(queryString: string = undefined): Observable<any[]>
  {
    const getQueryString = queryString !== undefined ? `${queryString}` : '';
    return this.http.get<any[]>(`/channels/minimal${getQueryString}`);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingLanguageChangedSubscription !== undefined) { this.messagingLanguageChangedSubscription.unsubscribe(); }
  }

  /**
   * Get all Channels from REST service.
   */
  public getChannels(performance: boolean): Observable<any[]>
  {
    return this.http.get<any[]>('/channels?performance=' + performance);
  }

  /**
   * Get all Channels from REST service.
   */
  public getActiveChannels(performance: boolean, active: boolean): Observable<any>
  {
    return this.http.get<any[]>('/channels?performance=' + performance + '&active=' + active);
  }

  /**
   * Get all Channels from REST service.
   */
  public getMinimalChannels(queryString: string = undefined): Observable<any[]>
  {
    const getQueryString = queryString !== undefined ? `${queryString}` : '';
    return this.http.get<any[]>(`/channels/minimal${getQueryString}`);
  }

  public getMinimalChannelById(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/channels/minimal/${id}`);
  }

  /**
   * Delete a Channel.
   *
   * @param id Channel id
   */
  public deleteChannel(id: number): Observable<any>
  {
    return this.http.delete<any>(`/channels/${id}`);
  }

  /**
   * Get the Channels where a member has not enrolled a Case.
   */
  public getNotEnrolledChannels(memberId: number): Observable<any[]>
  {
    if (this.allowMultipleChannelsEnrollment)
    {
      return this.getCacheMinimalChannels(`?nonPrivateOnly=true`);
    }
    else
    {
      return this.http.get<Channel[]>(`/channels/notenrolled/${memberId}`);
    }
  }

  /**
   * Get a Channel by ID from REST service.
   *
   * @param id Channel ID
   */
  public getChannel(id: number): Observable<Channel>
  {
    return this.http.get<Channel>('/channels/' + id);
  }

  /**
   * Get history entries of a channel.
   *
   * @param id Channel ID
   */
  public getHistory(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/channels/${id}/history`);
  }

  /**
   * Get statistics of a channel.
   *
   * @param id Channel ID
   */
  public getStatistics(id: number): Observable<any>
  {
    return this.http.get<any[]>(`/channels/${id}/statistics`);
  }

  /**
   * Get all tasks of a channel.
   *
   * @param id Channel ID
   */
  public getTasks(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/channels/${id}/tasks`);
  }

  /**
   * Get tasks summary of a channel.
   *
   * @param id Channel ID
   */
  public getTasksSummary(id: number): Observable<any[]>
  {
    return this.http.get<any[]>(`/channels/${id}/tasks/summary`);
  }

  /**
   * Persist a Channel in the database.
   *
   * @param data Data to save
   * @param isNew Is this a new Channel?
   */
  public saveChannel(data: Channel, isNew: boolean, channelId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/channels', data);
    }
    else
    {
      return this.http.put(`/channels/${channelId}`, data);
    }
  }

  public deleteChannelGoal(channelId: number, goalId: number): Observable<any>
  {
    return this.http.delete(`/channels/${channelId}/goal/${goalId}`);
  }

  public saveChannelGoal(goal, isNew: boolean, channelId: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post(`/channels/${channelId}/goal`, goal);
    }
    else
    {
      return this.http.put(`/channels/${channelId}/goal`, goal);
    }
  }

  /**
   * Get the Flowable BPM of a channel.
   *
   * @param modelId BPM ID
   */
  public getModel(modelId: string): Observable<any>
  {
    return this.http.get<any>(`/channels/model/${modelId}`);
  }

  /**
   * Get the Flowable BPM of a channel.
   *
   * @param channelId Channel ID
   * @param bpmn Business Process ID
   */
  public updateModel(channelId: number, bpmn: any): Observable<any>
  {
    return this.http.put(`/channels/bpmn/${channelId}`, bpmn);
  }

  /**
   * Get a Channel by Process Instance ID from REST service.
   *
   * @param id Process Instance ID
   */
  public getChannelByProcessInstance(id: string): Observable<Channel>
  {
    return this.http.get<Channel>('/channels/process/' + id);
  }

  /**
   * Get a Channel by Process Instance ID from REST service.
   *
   * @param id Process Instance ID
   */
  public getChannelPerformance(): Observable<any[]>
  {
    return this.http.get<any[]>('/channels/performance');
  }

  /**
   * Generate channel report.
   *
   * @param tasks Tasks to print.
   */
  public generateReport(channelId: string): Observable<any>
  {
    return this.http.get<any>('/channels/report/' + channelId);
  }

  /**
   * Return the Channel Status Key for translation.
   *
   * @param channelStatus Current channel status.
   */
  public getChannelStatusKey(channelStatus: string): string
  {
    let key = '';
    switch (channelStatus)
    {
      case 'Offline':
        key = 'OFFLINE';
        break;
      case 'Simulated':
        key = 'SIMULATED';
        break;
      case 'Online':
        key = 'ONLINE';
        break;
      case 'Deleted':
        key = 'DELETED';
        break;
      case 'Archived':
        key = 'ARCHIVED';
        break;
      case 'Template':
        key = 'TEMPLATE';
        break;
    }
    return key;
  }

  /**
   * Build a BPM default name based on channel name.
   * The BPM default name is splitting Channel Name into words and concatenating words until a maximum string length
   * is reached defined by MAXLENGTH.
   *
   * @param channelName Current Channel Name
   */
  public buildBPMDefaultName(channelName: string): string
  {
    const MAXLENGTH = 50;
    const words = channelName.split(' ');
    let defaultName = '';

    if (channelName && channelName.length > 0)
    {
      for (let i = 0; i <= words.length; i++)
      {
        if (defaultName.length <= MAXLENGTH &&
          // Check next words to see if it is still less than maximum length allowed. +1 is to account for the space.
          ((words.length >= i + 1) &&
            (defaultName.length + (words[i + 1] !== undefined ? words[i + 1].length : 0) + 1) <= MAXLENGTH))
        {
          defaultName += ((defaultName !== '' ? ' ' : '') + words[i]);
        }
        else
        {
          break;
        }
      }
    }
    return defaultName;
  }

  /**
   * Check if workflow status is in use.
   *
   * @param id Process Instance ID
   */
  public workflowStatusInUse(modelKey: string): Observable<boolean>
  {
    return this.http.get<boolean>('/channels/status/inuse/' + modelKey);
  }

  /**
   * Persist a Channel in the database.
   *
   * @param data Data to save
   * @param isNew Is this a new Channel?
   */
  public updateStatus(data: string, channelId: number): Observable<any>
  {
    return this.http.put(`/channels/${channelId}/status`, data);
  }

  /**
   * Load localizations
   */
  private loadLocalizations(): void
  {
    // Set the filter options localization
    this.translate.get([
      'PROGRAM-GROUPING-BY-PERFORMANCE',
      'PROGRAM-GROUPING-BY-STATUS',
      'PROGRAM-SORT-OPTIONS',
      'DASHBOARD-SORT-OPTIONS']).subscribe({
        next: (res) =>
        {
          let index = 0;
          for (const key in res)
          {
            if ({}.hasOwnProperty.call(res, key))
            {
              const splitted = res[key].split(',');
              for (let i = 0; i < splitted.length; i++)
              {
                switch (index)
                {
                  case 0:
                    this.channelPerformances[i].text = splitted[i];
                    break;
                  case 1: {
                    this.channelStatuses[i].text = splitted[i];
                    this.channelStatusesArchived[i].text = splitted[i];
                    break;
                  }
                  case 2:
                    this.channelSorts[i].text = splitted[i];
                    break;
                  case 3:
                    this.channelSortsDashboard[i].text = splitted[i];
                    break;
                }
              }
              index++;
            }
          }
        }
      });
  }

  private loadSettings(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.allowMultipleChannelsEnrollment = sessionSetting.allowMultipleChannelsEnrollment;
      }
    });
  }
}
